import { logger } from "../../datadog/logger";
import { normalizeError } from "../../utils";
import { VideoClient } from "./types";
import type {
  VideoClientEvent,
  VideoClientEventHandler,
} from "./VideoClientEvents";

/**
 * Singleton EventBus that intermediates Zoom VideoClient event broadcasting.
 *
 * Zoom uses a monolithic event emitter where all events are emitted through a single VideoClient
 * instance, unlike Twilio's granular approach with separate emitters per resource. This is a
 * pre-optimization based on performance and auditability issues surfaced during the technical spike.
 * For example, listening Twilio's RemoteAudio subscribed/unsubscribed/enabled events requires three
 * separate listeners on Zoom's single 'user-updated' event, and Twilio's Room
 * connected/reconnected/disconnected events are all handled by Zoom's single 'connection-changed' event.
 *
 * This EventBus implements a fan-out pattern that registers only one listener per Zoom event type
 * with the VideoClient and broadcasts to all handlers registered by our application code. This
 * addresses two important technical requirements: integrating Zoom's monolithic event listeners
 * in their recommended way while preserving our application's existing event listening paradigm.
 *
 * - Avoids MaxListenersExceededWarning through minimal listener registration
 * - Maintains existing application event handling patterns
 * - Enables future refactoring without breaking existing code
 */
export class ZoomEventBus {
  static #instance: ZoomEventBus | null = null;
  #videoClient: VideoClient;

  // register at most one event listener per Zoom event
  #zoomEventListeners = new Map<
    VideoClientEvent,
    VideoClientEventHandler<VideoClientEvent>
  >();

  // each registered event listener has list of handlers from application; Set preserves insertion order
  #applicationHandlers = new Map<
    VideoClientEvent,
    Set<VideoClientEventHandler<VideoClientEvent>>
  >();

  private constructor(videoClient: VideoClient) {
    this.#videoClient = videoClient;
  }

  static initialize(videoClient: VideoClient): void {
    if (this.#instance) {
      logger.warn("ZoomEventBus is already initialized");
      return;
    }
    this.#instance = new ZoomEventBus(videoClient);
  }

  static getInstance(): ZoomEventBus {
    if (!this.#instance) {
      throw new Error(
        "ZoomEventBus must be initialized first with initialize()",
      );
    }

    return this.#instance;
  }

  static tearDown(): void {
    if (this.#instance) {
      // unregister all bound handlers from VideoClient
      for (const [event, zoomEventListener] of this.#instance
        .#zoomEventListeners) {
        this.#instance.#videoClient.off(event, zoomEventListener);
      }

      // clear all internal state
      this.#instance.#applicationHandlers.clear();
      this.#instance.#zoomEventListeners.clear();

      this.#instance = null;
    }
  }

  on<T extends VideoClientEvent>(
    event: T,
    handler: VideoClientEventHandler<T>,
  ) {
    let handlersForEvent = this.#applicationHandlers.get(event);

    if (!handlersForEvent) {
      handlersForEvent = new Set();
      this.#applicationHandlers.set(event, handlersForEvent);

      // create closure that fans out to all current application handlers for this event
      const fanOutHandler = (...args: unknown[]) => {
        const handlersForEvent = this.#applicationHandlers.get(event);
        logger.debug("[ZoomEventBus] emitted event", {
          event,
          handlersInvoked: handlersForEvent?.size,
        });
        if (handlersForEvent) {
          for (const handler of handlersForEvent) {
            try {
              (handler as (...args: unknown[]) => void)(...args);
            } catch (error) {
              logger.error(
                "ZoomEventBus encountered an unhandled exception",
                { event, args },
                normalizeError(error),
              );
            }
          }
        }
      };

      this.#zoomEventListeners.set(event, fanOutHandler);
      this.#videoClient.on(event, fanOutHandler);
    }

    handlersForEvent.add(handler);
  }

  off<T extends VideoClientEvent>(
    event: T,
    handler: VideoClientEventHandler<T>,
  ) {
    const handlersForEvent = this.#applicationHandlers.get(event);

    if (handlersForEvent) {
      handlersForEvent.delete(handler);

      // if no more application handlers, unregister from Zoom
      if (handlersForEvent.size === 0) {
        this.#applicationHandlers.delete(event);

        const zoomEventListener = this.#zoomEventListeners.get(event);
        if (zoomEventListener) {
          this.#videoClient.off(event, zoomEventListener);
          this.#zoomEventListeners.delete(event);
        }
      }
    }
  }
}
